import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { decideOrderFaxSignature } from '../../services/orderFaxes';
import { allPagesOf, type BoardPage, type PageBoardSource } from './pageBoard';
import {
  assembleFaxDocument,
  editFaxDocumentPages,
  getFaxDocumentUrl,
  renameFaxDocument,
  splitFaxDocument,
} from '../../services/faxDocuments';
import {
  getReceivedFax,
  getReceivedFaxDocumentUrl,
} from '../../services/faxInbox';
import {
  listFaxTypeOptions,
  setClassification,
  type FaxTag,
  type FaxTypeOption,
} from '../../services/faxClassification';
import {
  renameFaxItem,
  type FaxItem,
  type FaxItemRef,
} from '../../services/faxItems';
import { archiveFaxItem, unarchiveFaxItem } from '../../services/faxRouting';
import { displayName } from './faxItemDisplay';
import {
  getBusinessErrorMessage,
  getErrorConflictCode,
} from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';

export const NONE = '__none__';

interface FaxItemEditorOptions {
  onError: (message: string) => void;
  /** Somebody else re-baked the document, so the page should reload it. */
  onConflict: () => void;
}

/** What the editable fields said when they were last loaded or saved. */
interface Baseline {
  name: string;
  typeId: string;
  tags: FaxTag[];
  pages: BoardPage[];
}

const refs = (chosen: BoardPage[]) =>
  chosen.map(({ source, page, rotation }) => ({ source, page, rotation }));

const pagesKey = (chosen: BoardPage[]) => JSON.stringify(refs(chosen));

const tagKey = (tags: FaxTag[]) =>
  JSON.stringify(tags.map(tag => tag.id).sort());

/**
 * One editor for anything in the inbox.
 *
 * Editing a document re-bakes it. Editing a transmission builds a document
 * out of it and leaves what arrived exactly as it arrived, still on record,
 * because the pages a sender actually transmitted are not ours to rewrite.
 *
 * Pages start in arrival order, oldest first, which is the opposite of the
 * list. A sender working through a stack faxes page one first, so oldest
 * first is the order they meant.
 */
export function useFaxItemEditor(
  item: FaxItem | null,
  { onError, onConflict }: FaxItemEditorOptions
) {
  const [sources, setSources] = useState<PageBoardSource[]>([]);
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [pages, setPages] = useState<BoardPage[]>([]);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [splitKeys, setSplitKeys] = useState<Set<string>>(new Set());
  const [splitName, setSplitName] = useState('');
  const [types, setTypes] = useState<FaxTypeOption[]>([]);
  const [typeId, setTypeId] = useState<string>(NONE);
  const [tags, setTags] = useState<FaxTag[]>([]);
  // Saving only rebuilds the document when the pages actually changed, so
  // opening a row to set its type does not quietly bake a new version of it.
  const [baseline, setBaseline] = useState<Baseline>({
    name: '',
    typeId: NONE,
    tags: [],
    pages: [],
  });
  const [archiving, setArchiving] = useState(false);
  // From a save until the row it saved reloads. The reload reseeds every
  // field, so anything typed in between would be silently lost.
  const [reloading, setReloading] = useState(false);

  // What is on screen, read when a refreshed row lands. Synced by the effect
  // below, which runs before the seeding one.
  const shown = useRef({ name, typeId, tags, baseline });
  const seededKey = useRef<string | null>(null);
  useEffect(() => {
    shown.current = { name, typeId, tags, baseline };
  }, [name, typeId, tags, baseline]);

  const seedFields = useCallback((row: FaxItem | null) => {
    const seeded = {
      name: row?.name ?? '',
      typeId: row?.fax_type_id ?? NONE,
      tags: row?.tags ?? [],
    };
    // A refreshed copy of the same row, such as one a returned-order match
    // filled in, keeps what the user changed and takes the rest from the row.
    const key = row ? `${row.kind}:${row.id}` : null;
    const sameRow = key !== null && key === seededKey.current;
    seededKey.current = key;
    const now = shown.current;
    setName(sameRow && now.name !== now.baseline.name ? now.name : seeded.name);
    setTypeId(
      sameRow && now.typeId !== now.baseline.typeId ? now.typeId : seeded.typeId
    );
    setTags(
      sameRow && tagKey(now.tags) !== tagKey(now.baseline.tags)
        ? now.tags
        : seeded.tags
    );
    setBaseline(current => ({ ...current, ...seeded }));
  }, []);

  // Restored from the row rather than blanked. Every field here is saved
  // whole on every save, so a blank field would clear what the row holds.
  useEffect(() => {
    seedFields(item);
    setReloading(false);
  }, [item, seedFields]);

  const hasItem = item !== null;
  useEffect(() => {
    if (!hasItem) return;
    let cancelled = false;
    void (async () => {
      try {
        const options = await listFaxTypeOptions();
        if (!cancelled) setTypes(options);
      } catch {
        if (!cancelled) onError('Could not load the types.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasItem, onError]);

  // A fax can hold a type that has since been retired. Without this the
  // select falls back to None and the next save reclassifies it as nothing.
  const typeOptions = useMemo(() => {
    if (!item?.fax_type_id) return types;
    if (types.some(type => type.id === item.fax_type_id)) return types;
    return [
      ...types,
      {
        id: item.fax_type_id,
        name: item.fax_type_name
          ? `${item.fax_type_name} (retired)`
          : 'Retired type',
      } as FaxTypeOption,
    ];
  }, [types, item?.fax_type_id, item?.fax_type_name]);

  /**
   * Answer for the page on screen. The order moves only on a yes, and either
   * answer is recorded against the person who gave it.
   */
  const decideSignature = useCallback(
    async (signed: boolean): Promise<boolean> => {
      const orderId = item?.answers_order_id;
      if (!orderId) return false;
      setDeciding(true);
      try {
        await decideOrderFaxSignature(orderId, signed);
        return true;
      } catch {
        onError('Could not record that answer.');
        return false;
      } finally {
        setDeciding(false);
      }
    },
    [item?.answers_order_id, onError]
  );

  // The pages reload only when the stored object can have changed, so a
  // refetch after a rename keeps the board as it is. An arrival that finished
  // arriving has pages it did not have before.
  const itemKind = item?.kind;
  const itemId = item?.id;
  const itemVersion = item?.version;
  const itemStatus = item?.status;
  useEffect(() => {
    if (!itemKind || !itemId) {
      setSources([]);
      setSourceIds([]);
      setPages([]);
      return;
    }
    let cancelled = false;
    setPreparing(true);
    setPages([]);
    setSources([]);
    // A fresh selection per thing opened, or pages ticked on the last one
    // would still be ticked against page keys that no longer exist.
    setSplitKeys(new Set());
    setSplitName('');
    void (async () => {
      try {
        if (itemKind === 'document') {
          const url = await getFaxDocumentUrl(itemId);
          if (cancelled) return;
          setSourceIds([]);
          setSources([{ url, pageCount: null }]);
          return;
        }
        // A transmission can carry several attachments, and only the ones that
        // finished downloading have bytes to show.
        const fax = await getReceivedFax(itemId);
        const ready = fax.documents.filter(
          document => document.status === 'ready'
        );
        const urls = await Promise.all(
          ready.map(document => getReceivedFaxDocumentUrl(fax.id, document.id))
        );
        if (cancelled) return;
        setSourceIds(ready.map(document => document.id));
        setSources(urls.map(url => ({ url, pageCount: null })));
      } catch {
        if (!cancelled) onError('Could not open those pages.');
      } finally {
        if (!cancelled) setPreparing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [itemKind, itemId, itemVersion, itemStatus, onError]);

  const onSourceLoaded = useCallback((index: number, pageCount: number) => {
    setSources(current => {
      if (current[index]?.pageCount === pageCount) return current;
      const next = current.map((source, position) =>
        position === index ? { ...source, pageCount } : source
      );
      // Seeded once every source has been read, so the rail starts as the whole
      // fax and the work is taking pages away rather than finding them.
      if (next.every(source => source.pageCount !== null)) {
        setPages(previous => {
          if (previous.length) return previous;
          const seeded = allPagesOf(next);
          setBaseline(current => ({ ...current, pages: seeded }));
          return seeded;
        });
      }
      return next;
    });
  }, []);

  /**
   * The stored object, in a new tab, scrollable the way a reader wants it.
   * For a transmission that is its first readable attachment.
   */
  const openOriginal = useCallback(async () => {
    if (!item) return;
    // Opened before the await: a window.open after an await has lost the user
    // gesture and is what pop-up blockers stop.
    const tab = window.open('', '_blank');
    if (!tab) {
      onError(
        'Your browser blocked the download. Allow pop-ups for this site and try again.'
      );
      return;
    }
    try {
      if (item.kind === 'document') {
        tab.location.href = await getFaxDocumentUrl(item.id);
        return;
      }
      const fax = await getReceivedFax(item.id);
      const ready = fax.documents.find(document => document.status === 'ready');
      if (!ready) {
        tab.close();
        onError('That fax has no pages that can be opened yet.');
        return;
      }
      tab.location.href = await getReceivedFaxDocumentUrl(fax.id, ready.id);
    } catch {
      tab.close();
      onError('Could not open that.');
    }
  }, [item, onError]);

  const toggleSplit = useCallback((key: string) => {
    setSplitKeys(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const taken = pages.filter(page => splitKeys.has(page.key));
  const kept = pages.filter(page => !splitKeys.has(page.key));
  // A document with no pages is not a document, so taking everything is
  // refused the same way taking nothing is.
  const canSplit =
    taken.length > 0 && kept.length > 0 && splitName.trim() !== '';

  /**
   * Separate the ticked pages into a fax of their own, and return what is
   * left: the same document one version on, or the document an arrival's
   * remaining pages became. Null when nothing was split.
   */
  const split = useCallback(async (): Promise<FaxItemRef | null> => {
    if (!item || !canSplit) return null;
    setSaving(true);
    try {
      let remaining: FaxItemRef;
      if (item.kind === 'document') {
        if (item.version === null) {
          onError('That document could not be split. Please reload and retry.');
          return null;
        }
        await splitFaxDocument(item.id, {
          name: splitName.trim(),
          version: item.version,
          taken: refs(taken),
          kept: refs(kept),
        });
        remaining = { kind: 'document', id: item.id };
      } else {
        // Nothing exists to split yet, so both halves are built from what
        // arrived and the transmission itself is left exactly as it arrived.
        await assembleFaxDocument({
          name: splitName.trim(),
          sources: sourceIds,
          pages: refs(taken),
        });
        const remainder = await assembleFaxDocument({
          name: name.trim() || splitName.trim(),
          sources: sourceIds,
          pages: refs(kept),
        });
        remaining = { kind: 'document', id: remainder.id };
      }
      setSplitKeys(new Set());
      setSplitName('');
      setReloading(true);
      return remaining;
    } catch (error) {
      if (getHttpStatus(error) === 409) {
        onConflict();
      } else {
        console.error('Failed to split', error);
        onError('Could not split those pages out.');
      }
      return null;
    } finally {
      setSaving(false);
    }
  }, [
    canSplit,
    item,
    kept,
    name,
    onConflict,
    onError,
    sourceIds,
    splitName,
    taken,
  ]);

  const pagesChanged =
    pages.length > 0 && pagesKey(pages) !== pagesKey(baseline.pages);

  // The patient is not part of this: the panel sends it with the filing.
  const dirty =
    !!item &&
    (name !== baseline.name ||
      typeId !== baseline.typeId ||
      tagKey(tags) !== tagKey(baseline.tags) ||
      pagesChanged);

  /** Put the fields and pages back to what was last loaded or saved. */
  const discard = useCallback(() => {
    setName(baseline.name);
    setTypeId(baseline.typeId);
    setTags(baseline.tags);
    if (baseline.pages.length) setPages(baseline.pages);
    setSplitKeys(new Set());
  }, [baseline]);

  /**
   * Save what is on screen and return the row it now lives on: the same row,
   * or the document an edited arrival became. Null when it failed, after
   * saying why.
   */
  const commit = useCallback(async (): Promise<FaxItemRef | null> => {
    if (!item) return null;
    const wanted = name.trim();
    setSaving(true);
    try {
      // Always, and first. It is the one thing that can be said about any row,
      // including a transmission whose pages are still downloading, and
      // assembling carries it across to the new document.
      await setClassification(item.kind, item.id, {
        fax_type_id: typeId === NONE ? null : typeId,
        patient_id: item.patient_id ?? null,
        admission_id: item.admission_id ?? null,
        intake_draft_id: item.intake_draft_id ?? null,
        tag_ids: tags.map(tag => tag.id),
      });

      let ref: FaxItemRef = { kind: item.kind, id: item.id };
      if (item.kind === 'document') {
        if (pagesChanged) {
          if (item.version === null) {
            // Every document has a version, so a null one means the row and
            // this screen disagree. Guessing would hide it.
            onError(
              'That document could not be saved. Please reload and retry.'
            );
            return null;
          }
          await editFaxDocumentPages(item.id, refs(pages), item.version);
        }
        // Renaming does not touch the version, so it cannot be refused by the
        // version check and does not need repeating after a page edit.
        if (wanted && wanted !== item.name) {
          await renameFaxDocument(item.id, wanted);
        }
      } else if (pagesChanged) {
        // Changing what arrived makes a document of it. The transmission keeps
        // its own pages, and the server carries the classification across.
        const created = await assembleFaxDocument({
          name: wanted || displayName(item),
          sources: sourceIds,
          pages: refs(pages),
        });
        ref = { kind: 'document', id: created.id };
      } else if (wanted && wanted !== (item.name ?? '')) {
        // Naming a transmission in place leaves it exactly as it arrived.
        await renameFaxItem({ kind: item.kind, id: item.id }, wanted);
      }
      // Reset here rather than on the reload, so a second click before the
      // reload lands finds nothing left to save.
      setBaseline(current => ({
        name,
        typeId,
        tags,
        pages: pages.length ? pages : current.pages,
      }));
      setReloading(true);
      return ref;
    } catch (error) {
      // 409 is the server refusing pages built against an older version: the
      // work is not wrong, it is aimed at a document that has moved.
      if (getErrorConflictCode(error) === 'FAX_NOT_ROUTABLE') {
        onError(
          getBusinessErrorMessage(error) ?? 'Could not save those changes.'
        );
      } else if (getHttpStatus(error) === 409) {
        onConflict();
      } else {
        console.error('Failed to save the fax', error);
        onError('Could not save those changes.');
      }
      return null;
    } finally {
      setSaving(false);
    }
  }, [
    item,
    name,
    onConflict,
    onError,
    pages,
    pagesChanged,
    sourceIds,
    tags,
    typeId,
  ]);

  /** Archive or un-archive. Returns what to tell the reader, or null. */
  const toggleArchive = useCallback(async (): Promise<string | null> => {
    if (!item) return null;
    setArchiving(true);
    try {
      if (item.archived_at) {
        await unarchiveFaxItem(item.kind, item.id);
        return 'Moved back to the inbox.';
      }
      await archiveFaxItem(item.kind, item.id);
      return 'Archived.';
    } catch (error) {
      console.error('Failed to archive the fax', error);
      onError('Could not archive this fax.');
      return null;
    } finally {
      setArchiving(false);
    }
  }, [item, onError]);

  return {
    item,
    sources,
    pages,
    setPages,
    onSourceLoaded,
    preparing,
    name,
    setName,
    typeId,
    setTypeId,
    typeOptions,
    tags,
    setTags,
    splitKeys,
    toggleSplit,
    splitName,
    setSplitName,
    taken,
    kept,
    canSplit,
    split,
    openOriginal,
    decideSignature,
    deciding,
    toggleArchive,
    archiving,
    saving,
    reloading,
    dirty,
    discard,
    commit,
  };
}

export type FaxItemEditor = ReturnType<typeof useFaxItemEditor>;
